import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormBuilder } from '@angular/forms';
import { _HttpClient } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  share,
  switchMap,
  takeUntil,
  Subject
} from 'rxjs';
import { OdataService } from 'src/app/shared/services/odata.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-select-base',
  template: '',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export abstract class SelectBaseComponent
  implements ControlValueAccessor, OnDestroy
{
  /**
   * Prefix used to indicate a valueField match request.
   * When this prefix is present in the search value, prerequisite conditions
   * (like isActive) are ignored to ensure records can be found even if inactive.
   */
  static readonly VALUEFIELD_MATCH_PREFIX = '__VALUEFIELD_MATCH__:';

  @ViewChild('selector') selector?: NzSelectComponent;
  odata = inject(OdataService);
  private fb = inject(FormBuilder);

  @Input() allowEdit = true;
  @Input() public disabled = false;
  get readonly() {
    if (this.disabled || !this.allowEdit) {
      return true;
    }
    return false;
  }

  /**
   * Please assign the value field if you need to use property value instead of the object itself.
   * for example: 'id' or 'code'
   */
  @Input() valueField = '';
  @Input() public allowClear = false;
  @Input() public addBackdrop = true;
  protected onListFilled: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(
    []
  );
  public value: any = undefined;
  private _isUpdating = false;
  onChange: any = () => {};
  onTouched: any = () => {};
  public searching = false;
  loading = false;
  public nzFilterOption = (): boolean => true;
  protected key = 'id';
  dropdownOpen: boolean | undefined = undefined;
  private searchDestroy$ = new Subject<void>();
  private currentSearchKeyword: string = '';

  // Add-New option value
  instantData: string = '';

  url = '';

  private _list: any[] = [];
  public get list(): any[] {
    return this._list;
  }
  public set list(v: any[]) {
    this._list = v;
    this.onListFilled.next(v);
  }

  private _selected: any;
  @Input() public get selected() {
    return this._selected;
  }
  public set selected(val) {
    this._selected = val;
    if (val && this.list.length === 0) {
      this.list = [val];
    }
  }
  @Output() readonly selectedChange = new EventEmitter<any>();

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() readonly onOpenChange = new EventEmitter<boolean>();

  @Input() borderless: boolean = false;

  searchForm = this.fb.group({
    keyword: [''],
    resultKeyword: ['']
  });
  constructor(
    protected http: _HttpClient,
    protected cdr: ChangeDetectorRef
  ) {
    // Create a shared observable from valueChanges
    // The form control updates immediately when setValue() is called (no debounce on form)
    // But we debounce only the HTTP requests to reduce server load
    const keywordChanges$ = this.searchForm.controls.keyword.valueChanges.pipe(
      distinctUntilChanged(), // Avoid duplicate updates
      share() // Share the observable so we can have multiple subscribers
    );

    // Debounced subscription for HTTP requests only
    // The form control itself updates immediately (no debounce)
    keywordChanges$
      .pipe(
        debounceTime(500), // Debounce only the HTTP requests
        distinctUntilChanged(), // Avoid duplicate requests for the same keyword
        switchMap(keyword => {
          // Cancel previous request and start new one
          // Track the current search keyword to ensure we only process matching results
          const keywordValue = keyword || '';
          const previousKeyword = this.currentSearchKeyword;
          this.currentSearchKeyword = keywordValue;

          // Log when a new search starts (and potentially cancels a previous one)
          if (previousKeyword !== keywordValue && !environment.production) {
            console.debug(
              `[SelectBase] New search started - Keyword: "${keywordValue}"${previousKeyword ? ` (Previous: "${previousKeyword}" was cancelled)` : ''}`
            );
          }

          // Clear the list immediately when keyword changes and is not empty
          // This prevents selecting old values from previous search results
          if (keywordValue && previousKeyword !== keywordValue) {
            this.list = [];
            this.cdr.detectChanges();
            if (!environment.production) {
              console.debug(
                `[SelectBase] List cleared for new search - Keyword: "${keywordValue}"`
              );
            }
          }

          this.loading = true;
          this.searching = true;
          this.cdr.detectChanges();

          this.url = this.getUrl(keywordValue);

          // Return an observable that includes the keyword for verification
          return this.http.get(this.url).pipe(
            // Map the response to include the keyword so we can verify it matches
            map(data => ({ data, keyword: keywordValue })),
            // Ensure this request is cancelled if a new search starts (switchMap handles this)
            finalize(() => {
              // Only update loading state if this is still the current search
              if (this.currentSearchKeyword === keywordValue) {
                this.loading = false;
                this.searching = false;
                this.cdr.detectChanges();
                if (!environment.production) {
                  console.debug(
                    `[SelectBase] Search completed - Keyword: "${keywordValue}"`
                  );
                }
              } else {
                // This request was cancelled by a newer search
                if (!environment.production) {
                  console.debug(
                    `[SelectBase] Search request cancelled - Keyword: "${keywordValue}" (Current keyword: "${this.currentSearchKeyword}")`
                  );
                }
              }
            })
          );
        }),
        takeUntil(this.searchDestroy$)
      )
      .subscribe({
        next: ({ data, keyword }) => {
          // Only process results if they match the current search keyword
          // This ensures outdated results are abandoned even if they arrive late
          if (keyword !== this.currentSearchKeyword) {
            // This result is outdated, ignore it
            if (!environment.production) {
              console.debug(
                `[SelectBase] Outdated result disregarded - Received keyword: "${keyword}", Current keyword: "${this.currentSearchKeyword}" (Result count: ${data?.value?.length || 0})`
              );
            }
            return;
          }

          const result: any = [...data.value];
          if (!environment.production) {
            console.debug(
              `[SelectBase] Processing valid result - Keyword: "${keyword}", Result count: ${result.length}`
            );
          }

          if (this.processData) {
            this.processData(result);
          }

          this.searchForm.controls.resultKeyword.setValue(keyword);
          this.list = result;

          this.cdr.detectChanges();

          // If there is search keyword and the dropdown is closed(user leaved the input search box already before search result returns)
          if (keyword && this.dropdownOpen === false) {
            // Find if there is exact matched result, which the matchType is 1 (stands for Equals)
            const exactMatch = result.find((x: any) => x.matchType === 1);
            if (exactMatch) {
              if (!environment.production) {
                console.debug(
                  `[SelectBase] Found exact match for keyword: "${keyword}"`,
                  exactMatch
                );
              }
              if (this.valueField)
                this.valueChanged(exactMatch[this.valueField]);
              else this.valueChanged(exactMatch);
            } else {
              this.valueChanged(null);
            }
          }
        },
        error: () => {
          this.loading = false;
          this.searching = false;
          this.cdr.detectChanges();
        }
      });
  }

  abstract getUrl(searchValue: string): string;

  getLoadMoreUrl() {
    if (!this.url) return '';
    if (this.url.indexOf('$skip=0') !== -1) {
      let pageUrl = this.url.replace('$skip=0', `$skip=${this.list.length}`);
      return pageUrl;
    }
    return '';
  }

  public async writeValue(value: any) {
    // Prevent recursive updates that could clear the selection
    if (this._isUpdating) {
      return;
    }

    this._isUpdating = true;
    this.value = value;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        await this.fetchData(value);
        this.selected = this.list.find(x => x[this.valueField] == value);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }

    this._isUpdating = false;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  valueChanged(val: any) {
    // Prevent recursive updates that could clear the selection
    if (this._isUpdating) {
      return;
    }

    this.value = val;
    if (this.valueField)
      this.selected = this.list.find(x => x[this.valueField] === val);
    else this.selected = val;
    this.onChange(val);
    this.selectedChange.emit(this.selected);
  }

  protected async openChanged(open: boolean) {
    this.dropdownOpen = open;

    // Reset the search keyword on dropdown open
    if (open) {
      await this.search('');
      this.onOpenChange.emit(open);
    }
  }

  protected setKeyword(value: string, maxLength: number = 10) {
    // Kelvin: Avoid clearing the search string on close, we should only clear the search keyword on open,
    // this is to avoid reset the search keyword to blank when user leaved the search box
    if (!value) return;

    // Remove leading and trailing spaces
    value = value.trim();

    // Trim the value to ensure it doesn't exceed maxLength
    const trimmedValue = value.substring(0, maxLength);

    // Update instantData with the trimmed value
    this.instantData = trimmedValue;

    // Update search keyword to the search form
    this.searchForm.controls.keyword.setValue(value);
  }

  protected search(value: string, maxLength: number = 10) {
    // Trim the value to ensure it doesn't exceed maxLength
    const trimmedValue = value.substring(0, maxLength);

    // Update instantData with the trimmed value
    this.instantData = trimmedValue;

    // Update search keyword to the search form
    this.searchForm.controls.keyword.setValue(value);
  }

  protected fetchData(keyword: string | null) {
    return new Promise<any[]>((resolve, reject) => {
      if (keyword === null) keyword = '';

      this.url = this.getUrl(keyword);

      this.loading = true;
      this.searching = true;

      //console.debug(`searching for ${keyword} with url: ${url}`);
      this.http
        .get(this.url)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.searching = false;
          })
        )
        .subscribe({
          next: data => {
            const result: any = [...data.value];

            if (this.processData) {
              this.processData(result);
            }

            this.searchForm.controls.resultKeyword.setValue(keyword);
            this.list = result;

            this.cdr.detectChanges();
            resolve(result);
          },
          error: () => {
            this.loading = false;
            this.searching = false;
            reject();
          }
        });
    });
  }

  protected processData(data: any[]) {
    return;
  }

  loadMore(): void {
    let pageUrl = this.getLoadMoreUrl();

    if (pageUrl) {
      this.loading = true;
      this.cdr.detectChanges();

      this.http
        .get(pageUrl)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe(res => {
          this.processData(res.value);
          this.list = [...this.list, ...res.value];
          this.cdr.detectChanges();
        });
    }
  }

  protected getValue(item: any) {
    if (this.valueField) {
      return item[this.valueField];
    }
    return item;
  }

  public ensureItemPresentList(item: any) {
    const index = this.list.findIndex(x => x[this.key] === item[this.key]);
    if (index >= 0) {
      this.list.splice(index, 1);
    }

    this.list = [item, ...this.list];
    // Use markForCheck instead of detectChanges to prevent form control recreation
    this.cdr.markForCheck();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    if (this.selector && this.selector.nzOpen) {
      const host = (<any>this.selector).host;
      if (!host) return;
      const rect = host.nativeElement.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth);

      if (!isVisible) {
        this.selector.setOpenState(false); // Close the dropdown if the element is not visible
      }
    }
  }

  ngOnDestroy(): void {
    // Clean up search subscription
    this.searchDestroy$.next();
    this.searchDestroy$.complete();
  }
}
